import { getAddress, type Address } from 'viem';

import { useTradingSessionStore } from '@/features/tradingSession/store';
import { isSessionExpired, useWalletStore } from '@/features/wallet/store';

import { useOAuthStore } from './store';

/**
 * The three places a login can name the EOA it was minted for, in the order the
 * backend would honour them.
 */
export interface AuthenticatedOwnerInputs {
    /** `owner` from an active trading-session status (the cookie session). */
    sessionStatusOwner?: string | null;
    /** `owner` correlated onto the stored application token. */
    appSessionOwner?: string | null;
    /** `owner` from the social-login projection (`GET /v2/auth/me`). */
    oauthOwner?: string | null;
}

function normalizeOwner(value: string | null | undefined): Address | null {
    if (typeof value !== 'string' || value.length === 0) return null;
    try {
        return getAddress(value);
    } catch {
        return null;
    }
}

/** Which of the three sources actually answered. */
export type AuthenticatedOwnerSource =
    'sessionStatus' | 'appSession' | 'oauth';

/** The owner, plus the source it was taken from. */
export interface AuthenticatedOwnerBinding {
    owner: Address;
    source: AuthenticatedOwnerSource;
}

/**
 * The canonical answer to "which EOA is the current login bound to", together
 * with where that answer came from.
 *
 * The trading-session status wins because it is the only source the backend
 * re-asserts on every read; the app-token correlation is next (it is minted from
 * whatever signed the challenge); the OAuth projection is the last resort, since
 * it survives a wallet switch that never touched the social account.
 *
 * The source matters to callers that act on the answer: an `oauth`-only owner is
 * a projection of the social account, not a statement about the wallet the user
 * is holding, so a disagreement with the connected wallet is far weaker evidence
 * than the same disagreement against a re-asserted session owner.
 *
 * A malformed value disqualifies only its own source (a garbage status owner
 * falls through to the app session rather than blanking the answer).
 */
export function resolveAuthenticatedOwnerBinding(
    inputs: AuthenticatedOwnerInputs,
): AuthenticatedOwnerBinding | null {
    const sessionStatus = normalizeOwner(inputs.sessionStatusOwner);
    if (sessionStatus) return { owner: sessionStatus, source: 'sessionStatus' };
    const appSession = normalizeOwner(inputs.appSessionOwner);
    if (appSession) return { owner: appSession, source: 'appSession' };
    const oauth = normalizeOwner(inputs.oauthOwner);
    if (oauth) return { owner: oauth, source: 'oauth' };
    return null;
}

/** {@link resolveAuthenticatedOwnerBinding} for callers that only want the EOA. */
export function resolveAuthenticatedOwner(
    inputs: AuthenticatedOwnerInputs,
): Address | null {
    return resolveAuthenticatedOwnerBinding(inputs)?.owner ?? null;
}

function useOwnerInputs(): AuthenticatedOwnerInputs {
    const status = useTradingSessionStore((state) => state.status);
    const session = useWalletStore((state) => state.session);
    const me = useOAuthStore((state) => state.me);

    return {
        sessionStatusOwner:
            status?.state === 'active' ? (status.owner ?? null) : null,
        appSessionOwner:
            session && !isSessionExpired(session)
                ? (session.owner ?? null)
                : null,
        oauthOwner: me?.authenticated ? (me.owner ?? null) : null,
    };
}

/**
 * React binding for {@link resolveAuthenticatedOwnerBinding}. Returns null when
 * nothing currently authenticated names an owner (an expired app session and an
 * inactive trading status contribute nothing).
 *
 * A fresh object every render, so read `owner` and `source` out of it rather
 * than depending on its identity.
 */
export function useAuthenticatedOwnerBinding(): AuthenticatedOwnerBinding | null {
    return resolveAuthenticatedOwnerBinding(useOwnerInputs());
}

/**
 * React binding for {@link resolveAuthenticatedOwner}. Returns null when nothing
 * currently authenticated names an owner.
 */
export function useAuthenticatedOwner(): Address | null {
    return resolveAuthenticatedOwner(useOwnerInputs());
}
